import { createGroup, createRow } from "./nodeFactories";
import { FilterItem, GroupFilter, RowFilter } from "./types";

// All actions supported by the query reducer.
// This reducer manages a tree structure of filters (rows + groups),
// allowing dynamic editing of a nested filter builder UI.
export type QueryAction =
	| { type: "ADD_ROW"; groupId: string }
	| { type: "ADD_GROUP"; groupId: string }
	| { type: "REMOVE_NODE"; nodeId: string }
	| { type: "UPDATE_ROW"; nodeId: string; payload: Partial<RowFilter> }
	| { type: "UPDATE_GROUP"; nodeId: string; payload: Partial<GroupFilter> }
	| { type: "RESET"; payload: FilterItem[] }
	| { type: "ADD_SAVED_GROUP"; groupId: string; group: GroupFilter };

// Main reducer entry point.
// Each action delegates to a dedicated recursive function that walks the tree
// and updates only the targeted node, keeping immutability intact.
export const queryReducer = (items: FilterItem[], action: QueryAction): FilterItem[] => {
	switch (action.type) {
		case "ADD_ROW":
			return addRow(items, action.groupId);

		case "ADD_GROUP":
			return addGroup(items, action.groupId);

		case "REMOVE_NODE":
			return removeNode(items, action.nodeId);

		case "UPDATE_ROW":
			return updateRow(items, action.nodeId, action.payload);

		case "UPDATE_GROUP":
			return updateGroup(items, action.nodeId, action.payload);

		case "RESET":
			return action.payload;

		case "ADD_SAVED_GROUP":
			return addSavedGroup(items, action.groupId, action.group);

		default:
			return items;
	}
};

// Adds a new empty row inside a target group.
// Traverses the tree recursively until it finds the matching groupId.
const addRow = (items: FilterItem[], groupId: string): FilterItem[] => {
	return items.map((item) => {
		if (item.type !== "group") return item;

		const group = item.value;

		if (group.id === groupId) {
			return {
				...item,
				value: {
					...group,
					rows: [...group.rows, createRow()],
				},
			};
		}

		return {
			...item,
			value: {
				...group,
				rows: addRow(group.rows, groupId),
			},
		};
	});
};

// Adds a nested group inside a target group.
// Includes a depth check to prevent exceeding maximum nesting level.
const addGroup = (items: FilterItem[], groupId: string): FilterItem[] => {
	return items.map((item) => {
		if (item.type !== "group") return item;

		const group = item.value;

		if (!canInsertGroup(items, groupId)) return item;

		if (group.id === groupId) {
			return {
				...item,
				value: {
					...group,
					rows: [...group.rows, createGroup()],
				},
			};
		}

		return {
			...item,
			value: {
				...group,
				rows: addGroup(group.rows, groupId),
			},
		};
	});
};

// Updates a single row node (filter/operator/value).
// Only the matching row is updated; all others are returned unchanged.
const updateRow = (items: FilterItem[], nodeId: string, payload: Partial<RowFilter>): FilterItem[] => {
	return items.map((item) => {
		if (item.type === "row") {
			if (item.value.id !== nodeId) return item;

			return {
				...item,
				value: {
					...item.value,
					...payload,
				},
			};
		}

		return {
			...item,
			value: {
				...item.value,
				rows: updateRow(item.value.rows, nodeId, payload),
			},
		};
	});
};

// Updates a group node (row operator or nested structure metadata).
const updateGroup = (items: FilterItem[], nodeId: string, payload: Partial<GroupFilter>): FilterItem[] => {
	return items.map((item) => {
		if (item.type !== "group") return item;

		if (item.value.id === nodeId) {
			return {
				...item,
				value: {
					...item.value,
					...payload,
				},
			};
		}

		return {
			...item,
			value: {
				...item.value,
				rows: updateGroup(item.value.rows, nodeId, payload),
			},
		};
	});
};

// Removes a node (row or group) from the tree.
// Ensures root group is never removed and avoids leaving empty groups.
const removeNode = (items: FilterItem[], nodeId: string): FilterItem[] => {
	if (items.length === 1 && items[0].type === "group" && items[0].value.id === nodeId) {
		return items;
	}

	const next = items
		.filter((item) => item.value.id !== nodeId)
		.map((item) => {
			if (item.type !== "group") return item;

			return {
				...item,
				value: {
					...item.value,
					rows: removeNode(item.value.rows, nodeId),
				},
			};
		});

	return next.map((item) => {
		if (item.type !== "group") return item;

		if (item.value.rows.length > 0) return item;

		return {
			...item,
			value: {
				...item.value,
				rows: [createRow()],
			},
		};
	});
};

// Inserts a saved group inside another group.
const addSavedGroup = (items: FilterItem[], groupId: string, savedGroup: GroupFilter): FilterItem[] => {
	return items.map((item) => {
		if (item.type !== "group") return item;

		const group = item.value;

		if (group.id === groupId) {
			return {
				...item,
				value: {
					...group,
					rows: [
						...group.rows,
						{
							type: "group",
							value: savedGroup,
						},
					],
				},
			};
		}

		return {
			...item,
			value: {
				...group,
				rows: addSavedGroup(group.rows, groupId, savedGroup),
			},
		};
	});
};

// Finds the depth level of a group inside the tree.
export const getGroupDepth = (items: FilterItem[], groupId: string, currentDepth = 0): number | null => {
	for (const item of items) {
		if (item.type !== "group") continue;

		if (item.value.id === groupId) return currentDepth;

		const nested = getGroupDepth(item.value.rows, groupId, currentDepth + 1);
		if (nested !== null) return nested;
	}

	return null;
};

// Prevents inserting groups beyond the maximum allowed nesting depth.
export const canInsertGroup = (items: FilterItem[], groupId: string, maxDepth = 3): boolean => {
	const depth = getGroupDepth(items, groupId);
	if (depth === null) return false;

	return depth < maxDepth - 1;
};
